import { Box, Grid, GridItem, Text } from "@chakra-ui/layout";
import { FC, useEffect, useMemo, useState } from "react";
import { prominent } from "color.js";
import { AssetTitle } from "domains/single-asset";
import { LinkIcon } from "lib/icons/link";
import { baseRESTUrl, isPreviewingPack, md } from "lib/utils";
import { Asset } from "lib/types";
import { isEditable } from "domains/single-asset/utils";
import { useLoggedinUser, usePack } from "lib/hooks";
import { useRouter } from "next/router";
import { usePackContext } from "domains/pack";
import { openLink } from "./utils";

const DEFAULT_COLOR: string = '255, 255, 255';

export const EmbeddedLinkAsset:FC<{ asset: Asset<any>}> = ({ asset }) => {
  const [ color, setColor ] = useState<string>(DEFAULT_COLOR);
  const { data: user } = useLoggedinUser();
  const { pathname } = useRouter();
  const { packId } = usePackContext();
  const { data: pack } = usePack(packId);

  const display: string = useMemo(() => {
    if (asset.data.has_screenshot) {
      return `${baseRESTUrl()}/v1/packs/${asset.pack_id}/files/${asset.id}.png?key=${encodeURI(asset.updated)}`;
    }

    return asset.data.image;
  }, [asset]);

  useEffect(() => {
    if (asset.data.has_screenshot) {
      return setColor(DEFAULT_COLOR);
    }

    (async() => {
      try {
        const color = await prominent(asset.data.display, { amount: 1 });
        setColor(color.toString());
      } catch (err) {
        setColor(DEFAULT_COLOR);
      }
    })();
  }, [asset.data.display, asset.data.has_screenshot]);

  const canEdit = isEditable(asset, user, pack) && !isPreviewingPack(pathname);

  return (
    <Grid
      position="relative"
      templateRows={["auto auto", "auto auto", "1fr"]}
      templateColumns={["1fr", "1fr", "repeat(2, calc(50% - 25px))"]}
      gap={["15px", "35px"]}
      minHeight="160px"
      alignItems="center"
      alignContent="center"
      onClick={!canEdit ? (e) => openLink(e, asset) : null}
    >
      <GridItem height="100%">
        <Box
          minHeight="160px"
          bgImage={`url(${display || '/document-placeholder@2x.png'})`}
          bgColor={`rgb(${color})`}
          bgSize="contain"
          bgRepeat="no-repeat"
          bgPos="center center"
          border="1px solid"
          borderColor="border.200"
          borderRadius="md"
        />
      </GridItem>
      <GridItem>
        <Grid templateColumns={["20px auto", "30px auto"]} gap="12px" alignItems="center">
          <LinkIcon fill="brand.500" iconHash={asset.data.icon_hash} assetId={asset.id} />
          <AssetTitle title={asset.data.title ? md.render(asset.data.title) : asset.data.url} />
        </Grid>
        <Box paddingLeft={["3px", "3px", "42px"]} mt="10px">
          {asset.data.description && <Text size="sm">{asset.data.description}</Text>}
        </Box>
      </GridItem>
    </Grid>
  )
}
